import Hero from '../components/Hero';
import SectionHeading from '../components/SectionHeading';
import PricingCard from '../components/PricingCard';
import { motion } from 'framer-motion';
import { Check, HelpCircle } from 'lucide-react';

const Subscription = () => {
  // Define features for each plan
  const basicFeatures = [
    { text: "Up to 20 sheep records", included: true },
    { text: "Comprehensive sheep records", included: true },
    { text: "Interactive flock lists", included: true },
    { text: "Cloud data storage", included: true },
    { text: "Mobile access", included: true },
    { text: "Movement records", included: true },
    { text: "Medical Records", included: true },
    { text: "Data Export", included: true },
    { text: "Showing records", included: true },
    { text: "Weight recording and lamb weights export", included: true },
    { text: "Pedigree sheep assessment", included: false },
  ];

  const professionalFeatures = [
    { text: "Up to 100 sheep records", included: true },
    { text: "Comprehensive sheep records", included: true },
    { text: "Interactive flock lists", included: true },
    { text: "Cloud data storage", included: true },
    { text: "Interactive pedigree tree", included: true },
    { text: "Movement records", included: true },
    { text: "Medical Records", included: true },
    { text: "Data Export", included: true },
    { text: "Weight recording and lamb weights export", included: true },
    { text: "Pedigree Sheep Assessment", included: true },
    { text: "On line help", included: true },
  ];

  const premiumFeatures = [
    { text: "Unlimited sheep records", included: true },
    { text: "Comprehensive sheep records", included: true },
    { text: "Interactive flock lists", included: true },
    { text: "Data import on set-up", included: true },
    { text: "Approved movement recording", included: true },
    { text: "Data Import", included: true },
    { text: "NEW - Import animals directly from society records", included: true },
    { text: "NEW - Weight & Profit Analysis reports", included: true },
    { text: "Weight recording and lamb weights export", included: true },
    { text: "Medical Records", included: true },
    { text: "Cloud data storage", included: true },
    { text: "Pedigree Sheep Assessment", included: true },
    { text: "On line help", included: true },
  ];

  return (
    <>
      <Hero 
        title="Subscription Plans"
        subtitle="Choose the right PedigreePro plan for your flock size and management needs."
        image="https://images.pexels.com/photos/995771/pexels-photo-995771.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />
      
      <section className="section bg-white">
        <div className="container">
          <SectionHeading 
            title="Choose Your Plan"
            subtitle="We offer flexible subscription options to meet the needs of all sheep breeders, from small hobby flocks to large commercial operations."
          />
          
          <div className="grid lg:grid-cols-3 gap-8 mt-8">
            <PricingCard 
              title="Basic"
              price="FREE"
              period="month"
              description="Perfect for small flocks and hobby breeders looking to replace their existing system."
              features={basicFeatures}
              buttonText="Get Started"
              buttonLink="https://pedigreepro.netlify.app/"
              delay={0.1}
            />
            
            <PricingCard 
              title="Professional"
              price="£3.95"
              period="month"
              description="Ideal if you want to keep pedigree records."
              features={professionalFeatures}
              buttonText="Get Started"
              buttonLink="https://pedigreepro.netlify.app/"
              highlighted
              delay={0.2}
            />
            
            <PricingCard 
              title="Premium"
              price="£7.95"
              period="month"
              description="For serious commercial breeders who need unlimited records and premium support."
              features={premiumFeatures}
              buttonText="Get Started"
              buttonLink="https://pedigreepro.netlify.app/"
              delay={0.3}
            />
          </div>
          
          <motion.div 
            className="mt-12 text-center text-neutral-600"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <p>No set-up costs, just dive in an start recording.</p>
            <p className="mt-2">When you reach your limited number, you can just upgrade to enter more.</p>
          </motion.div>
        </div>
      </section>
      
      <section className="section bg-neutral-50">
        <div className="container">
          <SectionHeading 
            title="Frequently Asked Questions"
            subtitle="Find answers to common questions about PedigreePro subscriptions."
          />
          
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-6 mt-8">
            <FaqItem 
              question="Can I switch plans later?" 
              answer="Yes, you can upgrade or downgrade your subscription at any time. When upgrading, you'll have immediate facility to enter more animals. When downgrading, you will still be able to see and work with the animal already in the system, just unable to enter more."
              delay={0.1}
            />
            
            <FaqItem 
              question="Why doesn’t the interactive pedigree tree work with Free version?" 
              answer="In actual fact, the interactive pedigree tree does work with the free version. However, to display a 4 generation pedigree tree requires 15 separate animals (each ancestor is represented by an induvial animal on the database) so as the FREE version is restricted to 20 animals, you would soon run out of space. So if you are intending to use the interactive pedigree tree, where you can populate as many generations as you wish, and click on any one of them in the tree to bring it forward and see all details, even for a small flock it is much better to have up to the Pro package which allows 100 animals in the database."
              delay={0.2}
            />
            
            <FaqItem 
              question="Do you work with breed associations?" 
              answer="This is something we are working on for a future release."
              delay={0.3}
            />
            
            <FaqItem 
              question="Can I import data from other software?" 
              answer="Yes, PedigreePro supports importing data from Excel spreadsheets and several other sheep management software systems. Our support team can help with the migration process which is included in the premium package."
              delay={0.4}
            />
            
            <FaqItem 
              question="Can I work with more than one pedigree flock?" 
              answer="Yes, PedigreePro is designed to work with muliple breeds."
              delay={0.5}
            />
            
            <FaqItem 
              question="Do you offer custom features?" 
              answer="Yes, for Premium subscribers, we can develop custom features tailored to your specific needs. Contact our development team to discuss your requirements."
              delay={0.6}
            />
             <FaqItem 
              question="What happens to my data if I cancel?" 
              answer="Your data remains in our system for 30 days after cancellation, giving you time to export it if needed. After 30 days, it will be permanently deleted for security and privacy reasons.."
              delay={0.3}
            />
            <FaqItem 
              question="Do I need a paid plan to record weights?" 
              answer="No. Weight recording, the shed weighing screen and the lamb weights Excel export are available on every plan, within your animal limit. The Weight & Profit Analysis report is a Premium feature."
              delay={0.4}
            />
            <FaqItem 
              question="Can I import weights from my EID stick reader?" 
              answer="Not yet, but it is planned. Importing weights from stick readers and files is coming soon for Premium subscribers."
              delay={0.5}
            />
            <FaqItem 
              question="Does the Weight & Profit Analysis show breeding values?" 
              answer="No. It shows the average performance of the lambs each ewe and ram has produced, with every lamb compared fairly against others of the same sex and birth type. Results based on only a few lambs should be treated with caution, and the report tells you how many lambs sit behind each figure."
              delay={0.6}
            />
          </div>
          
          <motion.div 
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            viewport={{ once: true }}
          >
            <p className="text-neutral-600 mb-6">
              Have more questions? Our team is here to help.
            </p>
            <a href="#" className="btn btn-primary">
              Contact Support
            </a>
          </motion.div>
        </div>
      </section>
      
      <section className="section bg-white">
        <div className="container">
          <SectionHeading 
            title="Enterprise Solutions"
            subtitle="We are developers, so custom solutions for large breeding operations, research institutions, and government organizations will always be considered."
          />
          
          <motion.div 
            className="bg-primary/10 rounded-xl p-8 border border-primary/20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="md:flex items-start gap-8">
              <div className="md:w-2/3">
                <h3 className="font-sans font-bold text-2xl mb-4 text-primary-dark">PedigreePro Enterprise</h3>
                <p className="text-neutral-700 mb-4">
                  For large-scale breeding operations, research institutions, and government organizations, we offer fully customized enterprise solutions with dedicated support, custom development, and advanced features.
                </p>
                <ul className="space-y-2 mb-6">
                  <EnterpriseFeature text="Unlimited records and users" />
                  <EnterpriseFeature text="Custom development for specific needs" />
                  <EnterpriseFeature text="API access for integration with other systems" />
                  <EnterpriseFeature text="Advanced data analytics and reporting" />
                  <EnterpriseFeature text="Dedicated account manager" />
                  <EnterpriseFeature text="24/7 premium support" />
                  <EnterpriseFeature text="On-site training and setup assistance" />
                </ul>
              </div>
              
              <div className="md:w-1/3 mt-6 md:mt-0">
                <div className="bg-white p-6 rounded-lg shadow-md text-center">
                  <p className="text-primary-dark font-sans font-medium mb-2">Custom Pricing</p>
                  <p className="text-neutral-600 mb-6">Tailored to your specific needs and scale</p>
                  <a href="#" className="btn btn-primary w-full">Request a Quote</a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

const FaqItem = ({ question, answer, delay = 0 }: { question: string; answer: string; delay?: number }) => (
  <motion.div 
    className="bg-white p-6 rounded-lg shadow-sm border border-neutral-200"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true, margin: "-100px" }}
  >
    <div className="flex items-start gap-2">
      <HelpCircle className="text-primary shrink-0 mt-1" size={20} />
      <div>
        <h3 className="font-sans font-semibold text-lg mb-2">{question}</h3>
        <p className="text-neutral-600">{answer}</p>
      </div>
    </div>
  </motion.div>
);

const EnterpriseFeature = ({ text }: { text: string }) => (
  <li className="flex items-center gap-2">
    <span className="text-primary">
      <Check size={18} />
    </span>
    <span className="text-neutral-700">{text}</span>
  </li>
);

export default Subscription;